import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';

export interface NutritionSummaryData {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

export interface NutritionGoals {
  calories?: number; // Single value
  proteinMin?: number; // Range
  proteinMax?: number; // Range
  carbsMin?: number; // Range
  carbsMax?: number; // Range
  fatMin?: number; // Range
  fatMax?: number; // Range
  fiber?: number; // Single value
}

interface NutritionSummaryCardProps {
  title: string;
  nutrition: NutritionSummaryData;
  goals?: NutritionGoals;
  icon?: LucideIcon;
  iconColor?: string;
  borderColor?: string;
  className?: string;
}

// Three color schemes to cycle through: red, orange, green
const colorSchemes = [
  {
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    textClass: 'text-red-700',
  },
  {
    bgClass: 'bg-orange-50',
    borderClass: 'border-orange-200',
    textClass: 'text-orange-700',
  },
  {
    bgClass: 'bg-green-50',
    borderClass: 'border-green-200',
    textClass: 'text-green-700',
  },
] as const;

// Nutrition metric configuration (without hardcoded colors)
const nutritionMetrics = [
  { 
    key: 'calories' as const, 
    label: 'Calories', 
    suffix: '' 
  },
  { 
    key: 'protein' as const, 
    label: 'Protein', 
    suffix: 'g' 
  },
  { 
    key: 'carbs' as const, 
    label: 'Carbs', 
    suffix: 'g' 
  },
  { 
    key: 'fat' as const, 
    label: 'Fat', 
    suffix: 'g' 
  },
  { 
    key: 'fiber' as const, 
    label: 'Fiber', 
    suffix: 'g' 
  },
] as const;

// Function to determine color based on nutrition value vs goal
const getColorForValue = (
  actual: number,
  goal?: number,
  goalMin?: number,
  goalMax?: number
): 'green' | 'orange' | 'red' => {
  // If no goals provided, default to green
  if (!goal && goalMin === undefined && goalMax === undefined) {
    return 'green';
  }

  // Single value comparison (calories, fiber)
  if (goal !== undefined && goalMin === undefined && goalMax === undefined) {
    if (goal === 0) return 'green'; // Avoid division by zero
    
    const percentageDiff = Math.abs((actual - goal) / goal) * 100;
    
    if (percentageDiff <= 10) {
      return 'green';
    } else if (percentageDiff <= 15) {
      return 'orange';
    } else {
      return 'red';
    }
  }

  // Range comparison (protein, carbs, fat)
  if (goalMin !== undefined && goalMax !== undefined) {
    // Handle case where min and max are the same (treat as single value)
    if (goalMin === goalMax) {
      if (goalMin === 0) return 'green'; // Avoid division by zero
      const percentageDiff = Math.abs((actual - goalMin) / goalMin) * 100;
      
      if (percentageDiff <= 10) {
        return 'green';
      } else if (percentageDiff <= 15) {
        return 'orange';
      } else {
        return 'red';
      }
    }

    // Calculate ±10% boundaries of min and max
    const minBoundary10 = goalMin * 0.9; // 10% below min
    const maxBoundary10 = goalMax * 1.1; // 10% above max
    
    // Calculate ±15% boundaries of min and max
    const minBoundary15 = goalMin * 0.85; // 15% below min
    const maxBoundary15 = goalMax * 1.15; // 15% above max
    
    // Check if value is within ±10% of both min and max
    if (actual >= minBoundary10 && actual <= maxBoundary10) {
      return 'green';
    }
    
    // Check if value is within ±15% of both min and max
    if (actual >= minBoundary15 && actual <= maxBoundary15) {
      return 'orange';
    }
    
    // Otherwise, it's red
    return 'red';
  }

  // Default to green if no valid comparison
  return 'green';
};

// Function to get color scheme based on color name
const getColorScheme = (color: 'green' | 'orange' | 'red') => {
  switch (color) {
    case 'green':
      return colorSchemes[2]; // Green
    case 'orange':
      return colorSchemes[1]; // Orange
    case 'red':
      return colorSchemes[0]; // Red
    default:
      return colorSchemes[2]; // Default to green
  }
};

// Helper component for nutrition metric card
const NutritionMetricCard = ({ 
  value, 
  label, 
  suffix,
  bgClass,
  borderClass,
  textClass
}: { 
  value: number; 
  label: string;
  suffix: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
}) => (
  <div className={`text-center p-3 ${bgClass} border ${borderClass} rounded-lg`}>
    <div className={`text-2xl font-bold ${textClass}`}>
      {Math.round(value)}{suffix}
    </div>
    <div className="text-xs text-gray-600 mt-1">{label}</div>
  </div>
);

const NutritionSummaryCard: React.FC<NutritionSummaryCardProps> = ({
  title,
  nutrition,
  goals,
  icon: Icon,
  iconColor = '',
  borderColor = 'border-gray-200',
  className = ''
}) => {
  // Function to get color for each metric
  const getMetricColor = (metricKey: string, actualValue: number): 'green' | 'orange' | 'red' => {
    if (!goals) {
      return 'green'; // Default to green if no goals provided
    }

    switch (metricKey) {
      case 'calories':
        return getColorForValue(actualValue, goals.calories);
      case 'protein':
        return getColorForValue(actualValue, undefined, goals.proteinMin, goals.proteinMax);
      case 'carbs':
        return getColorForValue(actualValue, undefined, goals.carbsMin, goals.carbsMax);
      case 'fat':
        return getColorForValue(actualValue, undefined, goals.fatMin, goals.fatMax);
      case 'fiber':
        return getColorForValue(actualValue, goals.fiber);
      default:
        return 'green';
    }
  };

  return (
    <Card className={`${borderColor} ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {Icon && <Icon className={`h-5 w-5 ${iconColor}`} />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-5 gap-3">
          {nutritionMetrics.map((metric) => {
            const actualValue = nutrition[metric.key];
            const colorName = getMetricColor(metric.key, actualValue);
            const colorScheme = getColorScheme(colorName);
            
            return (
              <NutritionMetricCard
                key={metric.key}
                value={actualValue}
                label={metric.label}
                suffix={metric.suffix}
                bgClass={colorScheme.bgClass}
                borderClass={colorScheme.borderClass}
                textClass={colorScheme.textClass}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default NutritionSummaryCard;

